/**
 * The **Board** tab: the cumulative cross-run board, one row per (canonical
 * protein, canonical mechanism).
 *
 * It takes the whole `useBoard` result rather than twenty separate props. That is
 * deliberate: this component *is* the board tab, so the tab's state is its input,
 * and enumerating the fields here would mean editing `App.tsx` — which is frozen —
 * every time the board grew a control.
 *
 * ── What this tab is, and what the run tab is ────────────────────────────────────
 *
 * `leaderboard.targets` is one run's ranking: immutable, per-run, and internally
 * comparable, because those targets played each other in one tournament. This board
 * is the projection *over* those rankings, derived from them and rebuilt on boot. So
 * two rows here can come from two different sweeps, and the tab is written throughout
 * as a **standing** rather than a contest: no versus band, no gap figure, no "beats".
 * See the docblocks in `BoardLeader` and `BoardTable` — the reasoning lives next to
 * the code that would be tempting to change.
 *
 * ── The three states, which are three states and not two ─────────────────────────
 *
 * `loading` with no rows is a skeleton; loaded with no rows is an invitation to run a
 * sweep; failed is `App.tsx`'s `FailedState`, already rendered above this component
 * from `cumulative.error`. The last of those is why this file must NOT fall through
 * to an empty state on error: "Nothing on the board yet" over a failed fetch is a
 * claim about the data, made by a component that never saw any.
 *
 * A reload keeps the rows on screen (`loading` with rows present), which is also what
 * keeps `BoardTable` mounted across a re-sort — the row stagger is first-paint-only
 * and unmounting the table would replay it on every sort.
 */
import type { TokenGetter } from '../api/leaderboard'
import { BoardLeader } from './BoardLeader'
import { BoardTable } from './BoardTable'
import { Pagination } from './Pagination'
import { Card, CardBody, CardHeader } from './ui/Card'
import { EmptyState, LoadingSkeleton } from './ui/States'
import type { useBoard } from '../hooks/useBoard'
import type { ReportsState } from '../hooks/useReports'

interface Props {
  cumulative: ReturnType<typeof useBoard>
  reports: ReportsState
  accountEmail: string
  getToken: TokenGetter
}

export function BoardTab({ cumulative, reports, accountEmail, getToken }: Props) {
  const hasRows = cumulative.entries.length > 0

  // First load. A skeleton and not an empty state: nothing is known yet, and the two
  // read identically to a user while meaning opposite things.
  if (cumulative.loading && !hasRows) {
    return (
      <Card>
        <CardHeader
          eyebrow="Cumulative board"
          title="Loading the board"
          description="One row per canonical protein and mechanism, derived from every run's ranking."
        />
        <CardBody>
          <LoadingSkeleton rows={8} />
        </CardBody>
      </Card>
    )
  }

  // Failed, with nothing cached to show. `App.tsx` already renders the API's own
  // message above this component; a second panel here would either repeat it or —
  // worse — replace it with an empty state that says the board is empty.
  if (!hasRows && cumulative.error) return null

  if (!hasRows) {
    // Which filters were on, in words, because an empty *filtered* board and an empty
    // board are different statements and only one of them is about the data. Saying
    // "no sweep has produced a ranked target yet" over a “Tier ≥ immediate” filter
    // would report the filter's effect as a finding.
    const active = [
      cumulative.minRuns > 1
        ? `identified by ${cumulative.minRuns} or more runs`
        : null,
      cumulative.tier ? `tiered ${cumulative.tier} or stronger` : null,
    ].filter((s): s is string => s !== null)

    return (
      <Card>
        <CardHeader
          eyebrow="Cumulative board"
          title={
            active.length > 0 ? 'Nothing matches this filter' : 'Nothing on the board yet'
          }
        />
        <CardBody>
          <EmptyState
            message={
              active.length > 0
                ? `No target on the board was ${active.join(' and ')}.`
                : 'No sweep has produced a ranked target yet.'
            }
            hint={
              active.length > 0
                ? 'Widen the filters above. A target only one run found, or one that reached only a weaker tier, is still a result — it is simply not a corroborated or a strong one.'
                : 'Start a sweep from the CLI or the API. The board is rebuilt from every run’s ranking, so the first finished sweep fills it.'
            }
          />
        </CardBody>
      </Card>
    )
  }

  return (
    <>
      <BoardLeader
        entries={cumulative.entries}
        total={cumulative.total}
        note={cumulative.note}
        sort={cumulative.sort}
        direction={cumulative.direction}
        offset={cumulative.offset}
      />
      <BoardTable
        entries={cumulative.entries}
        total={cumulative.total}
        sort={cumulative.sort}
        direction={cumulative.direction}
        note={cumulative.note}
        championsNote={cumulative.championsNote}
        history={cumulative.history}
        minRuns={cumulative.minRuns}
        onMinRunsChange={cumulative.setMinRuns}
        tier={cumulative.tier}
        onTierChange={cumulative.setTier}
        onSort={cumulative.toggleSort}
        onExpand={cumulative.loadHistory}
        reports={reports}
        retire={cumulative}
        accountEmail={accountEmail}
        getToken={getToken}
      />
      <Pagination
        total={cumulative.total}
        limit={cumulative.limit}
        offset={cumulative.offset}
        onOffsetChange={cumulative.setOffset}
        onPageSizeChange={cumulative.setPageSize}
        noun="board entries"
        disabled={cumulative.loading}
      />
    </>
  )
}
